import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { registerSchema } from '@rutatec/shared-schemas'
import { api } from '@/lib/api'
import type { z } from 'zod'

type RegisterInput = z.infer<typeof registerSchema>

type RoleChoice = 'REMITENTE_ADMIN' | 'CONDUCTOR_AUTONOMO' | 'TRANSPORTADORA_ADMIN'

const VEHICULO_TIPOS = [
  { value: 'FURGON',    label: 'Furgón',    kg: '≤ 1 t' },
  { value: 'CAMION_3T', label: 'Camión 3T', kg: '3 t'  },
  { value: 'CAMION_5T', label: 'Camión 5T', kg: '5 t'  },
  { value: 'CAMION_10T',label: 'Camión 10T',kg: '10 t' },
  { value: 'CAMION_20T',label: 'Camión 20T',kg: '20 t' },
]

function StepIndicator({ step, total }: { step: number; total: number }) {
  return (
    <div className="mb-6 flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold
            ${i < step ? 'bg-green text-white' : i === step ? 'bg-navy text-white' : 'bg-gray-100 text-gray-400'}`}>
            {i < step ? '✓' : i + 1}
          </div>
          {i < total - 1 && (
            <div className={`h-0.5 w-8 ${i < step ? 'bg-green' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  )
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-red">{message}</p>
}

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [roleChoice, setRoleChoice] = useState<RoleChoice | null>(null)
  const [serverError, setServerError] = useState('')
  const [success, setSuccess] = useState(false)

  const total = roleChoice ? 3 : 1

  const { register, handleSubmit, watch, trigger, formState: { errors } } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: { role: undefined },
    mode: 'onBlur',
  })

  const registerMutation = useMutation({
    mutationFn: (data: RegisterInput) => api.post('/auth/register', data).then(r => r.data),
    onSuccess: () => setSuccess(true),
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setServerError(msg ?? 'Error al registrar. Intente nuevamente.')
    },
  })

  const onSubmit = (data: RegisterInput) => {
    setServerError('')
    registerMutation.mutate(data)
  }

  const nextStep = async () => {
    const fieldsPerStep: (keyof RegisterInput)[][] = [
      ['nombre', 'apellido', 'email', 'phone', 'password', 'confirmPassword'],
      roleChoice === 'REMITENTE_ADMIN' || roleChoice === 'TRANSPORTADORA_ADMIN'
        ? ['empresaRazonSocial', 'empresaRuc', 'empresaCiudad']
        : ['conductorCedula', 'vehiculoTipo', 'vehiculoPlaca'],
    ]
    const ok = await trigger(fieldsPerStep[step] ?? [])
    if (ok) setStep(s => s + 1)
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-dark">
        <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-modal text-center">
          <div className="mb-4 text-5xl">✅</div>
          <h2 className="mb-2 font-display text-2xl font-bold text-navy">¡Cuenta creada!</h2>
          <p className="mb-6 font-body text-sm text-gray-DEFAULT">
            Revisa tu correo para verificar tu cuenta. El equipo de RUTATEC revisará tu perfil
            y recibirás acceso en breve.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full rounded-lg bg-navy py-3 font-body font-semibold text-white hover:bg-navy-dark transition-colors"
          >
            Ir al inicio de sesión
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-dark py-8">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="mb-6 text-center">
          <h1 className="font-display text-4xl font-bold tracking-wider">
            <span className="text-white">RUTA</span><span className="text-red">TEC</span>
          </h1>
          <p className="mt-1 font-body text-xs text-white/50">Crear cuenta nueva</p>
        </div>

        <div className="rounded-xl bg-white p-8 shadow-modal">

          {/* Paso 0 — elegir tipo de perfil */}
          {step === 0 && (
            <>
              <h2 className="mb-1 font-display text-xl font-bold text-navy">¿Quién eres?</h2>
              <p className="mb-6 font-body text-sm text-gray-DEFAULT">
                Selecciona tu perfil para configurar tu cuenta.
              </p>

              <div className="space-y-3">
                {([
                  {
                    role: 'REMITENTE_ADMIN' as const,
                    icon: '🏭',
                    title: 'Empresa remitente',
                    desc: 'Publico fletes y contrato transportistas',
                  },
                  {
                    role: 'CONDUCTOR_AUTONOMO' as const,
                    icon: '🚛',
                    title: 'Conductor autónomo',
                    desc: 'Tengo mi propio vehículo y busco cargas',
                  },
                  {
                    role: 'TRANSPORTADORA_ADMIN' as const,
                    icon: '🏢',
                    title: 'Transportadora',
                    desc: 'Gestiono una flota de conductores',
                  },
                ] as const).map(({ role, icon, title, desc }) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => {
                      setRoleChoice(role)
                      register('role').onChange({ target: { value: role, name: 'role' } })
                      setStep(1)
                    }}
                    className={`flex w-full items-start gap-4 rounded-lg border-2 p-4 text-left transition-all hover:border-navy hover:shadow-sm
                      ${roleChoice === role ? 'border-navy bg-navy/5' : 'border-gray-200'}`}
                  >
                    <span className="text-2xl">{icon}</span>
                    <div>
                      <p className="font-body font-semibold text-navy">{title}</p>
                      <p className="font-body text-xs text-gray-DEFAULT">{desc}</p>
                    </div>
                  </button>
                ))}
              </div>

              <p className="mt-6 text-center font-body text-sm text-gray-DEFAULT">
                ¿Ya tienes cuenta?{' '}
                <a href="/login" className="font-semibold text-navy hover:underline">Iniciar sesión</a>
              </p>
            </>
          )}

          {/* Paso 1 — datos personales */}
          {step === 1 && roleChoice && (
            <>
              <StepIndicator step={0} total={total} />
              <h2 className="mb-5 font-display text-xl font-bold text-navy">Datos personales</h2>

              <input type="hidden" {...register('role')} value={roleChoice} />

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Nombre *</label>
                    <input
                      {...register('nombre')}
                      className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                    />
                    <FieldError message={errors.nombre?.message} />
                  </div>
                  <div>
                    <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Apellido *</label>
                    <input
                      {...register('apellido')}
                      className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                    />
                    <FieldError message={errors.apellido?.message} />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Email *</label>
                  <input
                    {...register('email')}
                    type="email"
                    autoComplete="email"
                    className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                    placeholder="correo@empresa.com.py"
                  />
                  <FieldError message={errors.email?.message} />
                </div>

                <div>
                  <label className="mb-1 block font-body text-sm font-medium text-gray-dark">
                    Teléfono <span className="text-gray-400">(opcional)</span>
                  </label>
                  <input
                    {...register('phone')}
                    type="tel"
                    className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                    placeholder="+595981000000"
                  />
                  <FieldError message={errors.phone?.message} />
                </div>

                <div>
                  <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Contraseña *</label>
                  <input
                    {...register('password')}
                    type="password"
                    autoComplete="new-password"
                    className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                  />
                  <FieldError message={errors.password?.message} />
                  {watch('password') && !errors.password && (
                    <p className="mt-1 text-xs text-gray-400">✓ Mínimo 8 caracteres, una mayúscula y un número</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Confirmar contraseña *</label>
                  <input
                    {...register('confirmPassword')}
                    type="password"
                    autoComplete="new-password"
                    className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                  />
                  <FieldError message={errors.confirmPassword?.message} />
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(0)}
                  className="flex-1 rounded-lg border border-gray-200 py-2.5 font-body text-sm font-medium text-gray-DEFAULT hover:bg-gray-50"
                >
                  Atrás
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-[2] rounded-lg bg-navy py-2.5 font-body text-sm font-semibold text-white hover:bg-navy-dark transition-colors"
                >
                  Continuar →
                </button>
              </div>
            </>
          )}

          {/* Paso 2 — datos de empresa o vehículo */}
          {step === 2 && roleChoice && (
            <>
              <StepIndicator step={1} total={total} />

              {(roleChoice === 'REMITENTE_ADMIN' || roleChoice === 'TRANSPORTADORA_ADMIN') ? (
                <>
                  <h2 className="mb-5 font-display text-xl font-bold text-navy">Datos de la empresa</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Razón social *</label>
                      <input
                        {...register('empresaRazonSocial')}
                        className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                        placeholder="LOGÍSTICA CENTRAL S.A."
                      />
                      <FieldError message={errors.empresaRazonSocial?.message} />
                    </div>
                    <div>
                      <label className="mb-1 block font-body text-sm font-medium text-gray-dark">RUC *</label>
                      <input
                        {...register('empresaRuc')}
                        className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                        placeholder="1234567-8"
                      />
                      <FieldError message={errors.empresaRuc?.message} />
                    </div>
                    <div>
                      <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Ciudad *</label>
                      <input
                        {...register('empresaCiudad')}
                        className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                        placeholder="Asunción"
                      />
                      <FieldError message={errors.empresaCiudad?.message} />
                    </div>
                    <div>
                      <label className="mb-1 block font-body text-sm font-medium text-gray-dark">
                        Dirección <span className="text-gray-400">(opcional)</span>
                      </label>
                      <input
                        {...register('empresaDireccion')}
                        className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                        placeholder="Av. Aviadores del Chaco 2345"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="mb-5 font-display text-xl font-bold text-navy">Tu vehículo</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Cédula de identidad *</label>
                      <input
                        {...register('conductorCedula')}
                        className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                        placeholder="1234567"
                      />
                      <FieldError message={errors.conductorCedula?.message} />
                    </div>
                    <div>
                      <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Tipo de vehículo *</label>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {VEHICULO_TIPOS.map(vt => (
                          <label
                            key={vt.value}
                            className={`flex cursor-pointer flex-col items-center rounded-lg border-2 p-3 transition-all
                              ${watch('vehiculoTipo') === vt.value ? 'border-navy bg-navy/5' : 'border-gray-200 hover:border-gray-300'}`}
                          >
                            <input
                              type="radio"
                              {...register('vehiculoTipo')}
                              value={vt.value}
                              className="sr-only"
                            />
                            <span className="font-body text-sm font-semibold text-navy">{vt.label}</span>
                            <span className="font-body text-xs text-gray-400">{vt.kg}</span>
                          </label>
                        ))}
                      </div>
                      <FieldError message={errors.vehiculoTipo?.message} />
                    </div>
                    <div>
                      <label className="mb-1 block font-body text-sm font-medium text-gray-dark">Chapa del vehículo *</label>
                      <input
                        {...register('vehiculoPlaca')}
                        className="w-full rounded-lg border border-gray-silver px-3 py-2 font-body text-sm uppercase focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                        placeholder="ABC1234"
                        onInput={e => {
                          const el = e.currentTarget
                          el.value = el.value.toUpperCase()
                        }}
                      />
                      <FieldError message={errors.vehiculoPlaca?.message} />
                    </div>
                  </div>
                </>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 rounded-lg border border-gray-200 py-2.5 font-body text-sm font-medium text-gray-DEFAULT hover:bg-gray-50"
                >
                  Atrás
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-[2] rounded-lg bg-navy py-2.5 font-body text-sm font-semibold text-white hover:bg-navy-dark transition-colors"
                >
                  Continuar →
                </button>
              </div>
            </>
          )}

          {/* Paso 3 — confirmar y enviar */}
          {step === 3 && roleChoice && (
            <>
              <StepIndicator step={2} total={total} />
              <h2 className="mb-2 font-display text-xl font-bold text-navy">Confirmar registro</h2>
              <p className="mb-5 font-body text-sm text-gray-DEFAULT">
                Revisa tus datos antes de crear la cuenta.
              </p>

              <div className="rounded-lg bg-cream p-4 font-body text-sm space-y-1.5">
                <p><span className="font-medium text-gray-dark">Nombre:</span>{' '}
                  <span className="text-gray-600">{watch('nombre')} {watch('apellido')}</span></p>
                <p><span className="font-medium text-gray-dark">Email:</span>{' '}
                  <span className="text-gray-600">{watch('email')}</span></p>
                <p><span className="font-medium text-gray-dark">Perfil:</span>{' '}
                  <span className="text-gray-600">
                    {roleChoice === 'REMITENTE_ADMIN' ? 'Empresa remitente' :
                     roleChoice === 'TRANSPORTADORA_ADMIN' ? 'Transportadora' : 'Conductor autónomo'}
                  </span></p>
                {watch('empresaRazonSocial') && (
                  <p><span className="font-medium text-gray-dark">Empresa:</span>{' '}
                    <span className="text-gray-600">{watch('empresaRazonSocial')}</span></p>
                )}
                {watch('vehiculoPlaca') && (
                  <p><span className="font-medium text-gray-dark">Vehículo:</span>{' '}
                    <span className="text-gray-600">{watch('vehiculoTipo')} · {watch('vehiculoPlaca')}</span></p>
                )}
              </div>

              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 font-body text-xs text-amber-800">
                ⏳ Tu cuenta quedará en revisión. El equipo de RUTATEC verificará tus documentos
                antes de habilitarte para operar. El proceso toma entre 1 y 3 días hábiles.
              </div>

              {serverError && (
                <div className="mt-3 rounded-lg bg-red-light border border-red/20 p-3 text-sm text-red">
                  {serverError}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 rounded-lg border border-gray-200 py-2.5 font-body text-sm font-medium text-gray-DEFAULT hover:bg-gray-50"
                >
                  Atrás
                </button>
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={registerMutation.isPending}
                  className="flex-[2] rounded-lg bg-green py-2.5 font-body text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60 transition-colors"
                >
                  {registerMutation.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Creando cuenta...
                    </span>
                  ) : 'Crear cuenta'}
                </button>
              </div>
            </>
          )}
        </div>

        <p className="mt-6 text-center font-body text-xs text-white/40">
          © 2025 RUTATEC · Todos los derechos reservados
        </p>
      </div>
    </div>
  )
}
